/**
 * The projected finale: the stage the facilitator has reached, one block at a time (replay → AI banks → deal call → recognitions → rank).
 * Rank is drawn only under stage `rank`, whatever else the view carries (OD5-5).
 */
import { FINALE_STAGES, type FinaleView } from "@bsim/contracts";
import { useRoute } from "../api/context";
import { DEAL_DECISION_WORDS, DEAL_VS_RANK_NOTE, FINALE_LABELS } from "./labels";
import { NoOffer } from "./NoOffer";
import { RECOGNITION_LABELS } from "../score/labels";
import { rankingWords } from "../facilitator/derive";
import { formatPoints, formatUsdAShare, formatUsdPerShare } from "../format";
import { GxScoreboard } from "../score/GxScoreboard";
import { anonymised, GxReplayChart } from "../charts/ReplayChart";
import { GxNotes } from "../theme/GxNotes";
import { useHiddenAiSeats } from "../facilitator/useAiSeats";

/** The chart is drawn at 1/ZOOM of the stage and zoomed up so its axis type reads from the back of the room. */
const ZOOM = 1.6;
/** The ranking is a table with a caption of its own: a little smaller so the board, its note and the caption all show in full. */
const RANK_ZOOM = 1.45;
/** The replay fills the stage's body: 1680px of stage width and about 480px of height, drawn at 1/ZOOM. */
const CHART_W = 1050;
const CHART_H = 300;

function Replay({ cohortId, stage, replay: raw }: { readonly cohortId: string; readonly stage: FinaleView["stage"]; readonly replay: NonNullable<FinaleView["replay"]> }) {
  const replay = anonymised(raw, useHiddenAiSeats(cohortId, stage));
  return (
    <div className="gx-room-replay gx-theme-dark" data-testid="room.finale.replay">
      <p className="gx-room-lead" data-testid="room.finale.lead">How much value each bank created against the status-quo twin, year by year (the last years are looked through).</p>
      <div className="gx-room-chart" style={{ zoom: ZOOM }}>
        <GxReplayChart replay={replay} width={CHART_W} height={CHART_H} legend={false} endNames testId="room.finale.replay.frame" />
      </div>
    </div>
  );
}

/** Dollars lead, points follow: "+$4.24 a share (+7.72 pts)". Without the board's dollar figure the points stand alone. */
function valueVsTwin(rate: number, usdPerShare: number | undefined): string {
  const pts = formatPoints(rate * 100, { signed: true, short: true });
  return usdPerShare === undefined ? pts : `${formatUsdAShare(usdPerShare, { signed: true })} (${pts})`;
}

function Ranking({ cohortId, ranking }: { readonly cohortId: string; readonly ranking: NonNullable<FinaleView["ranking"]> }) {
  const board = useRoute("cohortBoard", { cohortId });
  const ranked = board.data?.cohort.ranking;
  // The room ranking carries no seats or AI flags: the team id stands in for the seat, and nobody is "you" on the projector.
  const banks = ranking.map((r) => ({ teamId: r.teamId, seatId: r.teamId, name: r.name, isAi: null, rank: r.rank, score: r.score, gateRankedLast: r.gateRankedLast }));
  return (
    <div className="gx-room-ranking" data-testid="room.finale.ranking">
      {ranked === undefined ? null : <p className="gx-room-subtitle" data-testid="room.finale.ranking.basis">{rankingWords(ranked)}</p>}
      <div className="gx-room-board gx-theme-dark" style={{ zoom: RANK_ZOOM }}><GxScoreboard banks={banks} youSeatId={null} variant="finale" /></div>
    </div>
  );
}

export function Stage({ cohortId, finale }: { readonly cohortId: string; readonly finale: FinaleView }) {
  const market = useRoute("publicMarket", { cohortId });
  const scoreOf = new Map((market.data?.banks ?? []).flatMap((b) => (b.score === null || b.score === undefined ? [] : [[b.teamId, b.score.valueCreatedUsdPerShare] as const])));
  switch (finale.stage) {
    case "replay":
      return finale.replay === null ? null : <Replay cohortId={cohortId} stage={finale.stage} replay={finale.replay} />;
    case "aiReveal":
      return finale.aiBanks === null ? null : (
        <ul className="gx-room-cards gx-room-cards--ai" data-testid="room.finale.ai">
          {finale.aiBanks.map((b) => <li key={b.teamId}><b>{b.name}</b><em>{b.personaName}</em><span>{b.playbook}</span><span className="gx-num">{valueVsTwin(b.valueCreatedRate, scoreOf.get(b.teamId))} against its twin</span></li>)}
        </ul>
      );
    case "dealCall":
      if (finale.dealCalls === null) return null;
      // Nobody got an offer (the card never fired): say so, rather than a heading over nothing.
      return finale.dealCalls.length === 0 ? <NoOffer replay={finale.replay} testId="room.finale.deal" /> : (
        <div data-testid="room.finale.deal" className="gx-room-deal">
        <ul className="gx-room-cards gx-room-cards--feature gx-room-cards--deal">
          {finale.dealCalls.map((d) => (
            <li key={d.teamId}>
              <b>{d.name}</b>
              <span>{DEAL_DECISION_WORDS[d.dealCall.decision]} the round {d.dealCall.offeredRound} offer of {formatUsdPerShare(d.dealCall.offerUsdPerShare)}/share</span>
              <strong className="gx-room-figure gx-num">{formatUsdPerShare(d.dealCall.dealCallDisplayUsdPerShare, { signed: true })}<small>/share in today's money</small></strong>
              <span className="gx-num">On its own plan {formatUsdPerShare(d.dealCall.teamWealthUsdPerShare)} a share; its twin {formatUsdPerShare(d.dealCall.autopilotWealthUsdPerShare)}.</span>
            </li>
          ))}
        </ul>
        <div className="gx-theme-dark gx-room-note"><GxNotes testId="room.finale.deal.note" notes={[DEAL_VS_RANK_NOTE]} /></div>
        </div>
      );
    case "recognitions":
      return finale.recognitions === null ? null : (
        <ul className="gx-room-cards gx-room-cards--feature" data-testid="room.finale.recognitions">
          {finale.recognitions.map((r) => <li key={r.kind}><em>{RECOGNITION_LABELS[r.kind]}</em><b>{r.teamNames.join(", ")}</b><span>{r.citation}</span></li>)}
        </ul>
      );
    case "rank":
      return finale.ranking === null ? null : <Ranking cohortId={cohortId} ranking={finale.ranking} />;
    case "notStarted":
      return <p className="gx-room-prompt gx-room-prompt--centre">The finale begins in a moment.</p>;
  }
}

export function Finale({ cohortId, finale }: { readonly cohortId: string; readonly finale: FinaleView }) {
  const steps = FINALE_STAGES.filter((s) => s !== "notStarted");
  const at = FINALE_STAGES.indexOf(finale.stage);
  // The deal call with no offer is one question for the room: centred in the box, not a lone line at the top.
  // The recognitions and the deal calls are a few big cards: centred in the box, not a row at the top over empty navy.
  const centred = finale.stage === "notStarted" || finale.stage === "dealCall" || finale.stage === "recognitions";
  return (
    <section className="gx-room-main gx-room-finale" data-testid="room.finale" data-stage={finale.stage}>
      <div className="gx-room-eyebrow">Finale</div>
      <h1 className="gx-room-headline gx-room-headline--sm">{FINALE_LABELS[finale.stage]}</h1>
      <div className={`gx-room-finale-body${centred ? " gx-room-finale-body--centre" : ""}`}><Stage cohortId={cohortId} finale={finale} /></div>
      <ol className="gx-room-steps" aria-label="Finale stages">
        {steps.map((s) => <li key={s} data-status={FINALE_STAGES.indexOf(s) < at ? "done" : s === finale.stage ? "now" : "todo"}>{FINALE_LABELS[s]}</li>)}
      </ol>
    </section>
  );
}
